import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi, errMsg } from "../api";
import { Field, TextInput, TextArea, SaveBar } from "../components";
import { toast } from "../../components/ui/sonner";

const NICE = {
  home: "Homepage sections", about: "About (hub)", "who-we-are": "Who We Are",
  "mission-and-values": "Mission & Values", "why-lemur-quality": "Why Lemur Quality",
  "our-approach": "Our Approach", "privacy-policy": "Privacy Policy",
  "terms-of-use": "Terms of Use", "cookie-policy": "Cookie Policy",
};

export function PagesList() {
  const [pages, setPages] = useState([]);
  useEffect(() => { adminApi.get("/admin/pages").then((r) => setPages(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  return (
    <div data-testid="admin-pages-list">
      <div className="adm-head">
        <div><h1>Pages</h1><div className="sub">Hero text and content sections for every editable page</div></div>
      </div>
      <div className="adm-card" style={{ padding: 0 }}>
        <table className="adm-table">
          <thead><tr><th>Page</th><th>Slug</th><th>Title</th><th style={{ width: 100 }}></th></tr></thead>
          <tbody>
            {pages.map((p) => (
              <tr key={p.slug}>
                <td style={{ fontWeight: 600 }}>{NICE[p.slug] || p.slug}</td>
                <td className="mono">{p.slug}</td>
                <td>{p.title}</td>
                <td><Link className="adm-btn ghost sm" to={`/admin/pages/${p.slug}`} data-testid={`edit-page-${p.slug}`}>Edit</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PageEditor() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.get(`/admin/pages/${slug}`).then((r) => setPage(r.data)).catch((e) => toast.error(errMsg(e))); }, [slug]);
  if (!page) return <div className="adm-empty">Loading…</div>;

  const set = (k, v) => setPage((p) => ({ ...p, [k]: v }));
  const setSeo = (k, v) => setPage((p) => ({ ...p, seo: { ...(p.seo || {}), [k]: v } }));
  const setSection = (i, patch) => set("sections", page.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = page;
      await adminApi.put(`/admin/pages/${slug}`, payload);
      toast.success("Page saved — live on the public site");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`admin-page-editor-${slug}`}>
      <div className="adm-head">
        <div>
          <h1>Edit: {NICE[slug] || slug}</h1>
          <div className="sub"><Link to="/admin/pages">← Back to pages</Link></div>
        </div>
        <SaveBar onSave={save} saving={saving} testid="save-page" />
      </div>

      <div className="adm-card">
        <h2>Hero</h2>
        <div className="adm-grid cols-2">
          <Field label="Eyebrow"><TextInput value={page.eyebrow} onChange={(v) => set("eyebrow", v)} data-testid="page-eyebrow" /></Field>
          <Field label="Title"><TextInput value={page.title} onChange={(v) => set("title", v)} data-testid="page-title" /></Field>
        </div>
        <Field label="Lead / subtitle"><TextArea value={page.lead} onChange={(v) => set("lead", v)} rows={2} data-testid="page-lead" /></Field>
        {page.mission_title !== undefined && (
          <>
            <Field label="Mission title"><TextInput value={page.mission_title} onChange={(v) => set("mission_title", v)} /></Field>
            <Field label="Mission text"><TextArea value={page.mission_text} onChange={(v) => set("mission_text", v)} rows={3} /></Field>
          </>
        )}
        {page.updated !== undefined && (
          <Field label="Last updated label"><TextInput value={page.updated} onChange={(v) => set("updated", v)} /></Field>
        )}
      </div>

      <div className="adm-card">
        <h2>Content sections</h2>
        {(page.sections || []).map((sec, i) => (
          <div key={i} className="list-row" style={{ marginBottom: 10 }}>
            <div className="fields">
              <input value={sec.heading || ""} placeholder="Section heading" onChange={(e) => setSection(i, { heading: e.target.value })} data-testid={`section-heading-${i}`} />
              <textarea rows={5} value={(sec.paragraphs || []).join("\n\n")} placeholder="Paragraphs — blank line separates them"
                onChange={(e) => setSection(i, { paragraphs: e.target.value.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) })}
                data-testid={`section-paragraphs-${i}`} />
            </div>
            <button type="button" className="rm" onClick={() => set("sections", page.sections.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <button type="button" className="adm-btn ghost sm" onClick={() => set("sections", [...(page.sections || []), { heading: "", paragraphs: [] }])} data-testid="add-section">+ Add section</button>
      </div>

      <div className="adm-card">
        <h2>SEO</h2>
        <div className="adm-grid cols-2">
          <Field label="Meta title"><TextInput value={page.seo?.title} onChange={(v) => setSeo("title", v)} /></Field>
          <Field label="Meta description"><TextInput value={page.seo?.description} onChange={(v) => setSeo("description", v)} /></Field>
        </div>
      </div>

      <SaveBar onSave={save} saving={saving} testid="save-page-bottom" />
    </div>
  );
}
